import type { ChatSummary } from '../api/types'

export interface RepoChatGroup {
  repoId: string
  repoName: string
  repoOwner?: string | null
  workspaceId?: string
  chats: ChatSummary[]
  latestUpdatedAt: number
}

export interface GroupedChats {
  pinned: ChatSummary[]
  repoGroups: RepoChatGroup[]
  recent: ChatSummary[]
}

/**
 * Compares two chats using backend listing order:
 * Pinned chats first (newest pinned first, tie-break by updatedAt), then recent chats (newest updated first).
 */
export function compareChatSummaries(a: ChatSummary, b: ChatSummary): number {
  const aPinned = a.pinnedAt != null ? 1 : 0
  const bPinned = b.pinnedAt != null ? 1 : 0
  if (aPinned !== bPinned) return bPinned - aPinned
  if (aPinned && bPinned) {
    const pinDiff = (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0)
    if (pinDiff !== 0) return pinDiff
  }
  return b.updatedAt - a.updatedAt
}

/**
 * Groups and sorts chat items for the sidebar navigation:
 * - `pinned`: chats with `pinnedAt != null`, sorted newest pinned first (`pinnedAt DESC`)
 * - `repoGroups`: unpinned chats with `repoId`, grouped by repository, sorted by latest activity (`latestUpdatedAt DESC`)
 * - `recent`: unpinned chats without `repoId`, sorted newest updated first (`updatedAt DESC`)
 */
export function groupChatsForSidebar(items: ChatSummary[]): GroupedChats {
  const pinned: ChatSummary[] = []
  const recent: ChatSummary[] = []
  const repoMap = new Map<string, RepoChatGroup>()

  for (const item of items) {
    if (item.pinnedAt != null) {
      pinned.push(item)
    } else if (item.repoId) {
      let group = repoMap.get(item.repoId)
      if (!group) {
        group = {
          repoId: item.repoId,
          repoName: item.repoName || item.repoId,
          repoOwner: item.repoOwner ?? null,
          workspaceId: item.workspaceId,
          chats: [],
          latestUpdatedAt: 0
        }
        repoMap.set(item.repoId, group)
      }
      group.chats.push(item)
      if (item.updatedAt > group.latestUpdatedAt) {
        group.latestUpdatedAt = item.updatedAt
      }
      if (!group.repoOwner && item.repoOwner) group.repoOwner = item.repoOwner
      if ((!group.repoName || group.repoName === item.repoId) && item.repoName) {
        group.repoName = item.repoName
      }
    } else {
      recent.push(item)
    }
  }

  pinned.sort((a, b) => (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0) || b.updatedAt - a.updatedAt)
  recent.sort((a, b) => b.updatedAt - a.updatedAt)

  const repoGroups = Array.from(repoMap.values())
  for (const group of repoGroups) {
    group.chats.sort((a, b) => b.updatedAt - a.updatedAt)
  }
  repoGroups.sort((a, b) => b.latestUpdatedAt - a.latestUpdatedAt)

  return { pinned, repoGroups, recent }
}
